import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronRight, Lock, Eye, FileText, CheckCircle, Mail } from "lucide-react";
import { getPrivacyContent, getLocalPrivacyContent, getSeoSettings, getLocalSeoSettings } from "../api";
import FormattedText from "../components/FormattedText";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  const [content, setContent] = useState(() => getLocalPrivacyContent());
  const [seo, setSeo] = useState(() => getLocalSeoSettings());

  useEffect(() => {
    getPrivacyContent().then((data) => {
      if (data) setContent(data);
    }).catch(() => {});

    getSeoSettings().then((data) => {
      if (data) setSeo(data);
    }).catch(() => {});
  }, []);

  const title = content?.title || "Privacy Policy";
  const lastUpdated = content?.lastUpdated || "September 2026";
  const contactEmail = content?.contactEmail || seo?.contactEmail || "privacy@minigolfalabama.com";
  const siteDomain = seo?.siteUrl ? seo.siteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") : "minigolfalabama.com";
  const siteName = seo?.siteName || "Mini Golf Alabama";

  const introParagraphs = content?.introParagraphs && content.introParagraphs.length > 0
    ? content.introParagraphs
    : [
        `Welcome to ${siteName} ("we," "our," or "us"), accessible via ${siteDomain}. We value your privacy and are committed to protecting the personal information you may share while browsing our directory of mini golf and putt-putt courses across the United States.`,
        `This Privacy Policy explains what information we collect, how we use it, how we safeguard it, and your rights regarding your data when you visit our website.`
      ];

  const sections = content?.sections && content.sections.length > 0
    ? content.sections
    : [
        {
          title: "1. Information We Collect",
          content: `When you visit ${siteDomain}, our servers automatically record standard browser data, such as your IP address, browser type, operating system, referring URLs, pages viewed, and the date/time of your visit. We also collect contact information when you reach out to us.`
        },
        {
          title: "2. Cookies and Web Technologies",
          content: "We use essential cookies, local storage, and similar technologies to enhance your browsing experience, remember your preferences, and monitor site performance."
        },
        {
          title: "3. Third-Party Links & Venue Websites",
          content: "Our directory contains links to external venue websites, booking platforms, and social networks. We do not control the privacy practices of external third-party sites."
        },
        {
          title: "4. Data Security & Storage",
          content: "We implement industry-standard encryption, SSL/TLS protocols, and secure storage to safeguard your data against unauthorized access, alteration, or disclosure."
        }
      ];

  return (
    <div className="min-h-[70vh] bg-[#f8fafc]" data-testid="privacy-policy-page">
      <SEO
        title={`${title} | ${siteName}`}
        description={`Read the Privacy Policy for ${siteName} (${siteDomain}) to understand how we collect, protect, and use information.`}
        canonicalUrl={`https://${siteDomain}/privacy-policy`}
        ogType="website"
      />
      {/* Header Banner */}
      <div className="bg-[#181a20] text-white border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-[#e5243b] font-medium">{title}</span>
          </nav>
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-red-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#e5243b]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight">
                {title}
              </h1>
              <p className="mt-1 text-sm text-gray-400">
                Last updated: <span className="text-gray-200 font-medium">{lastUpdated}</span> · Effective for {siteDomain}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12 shadow-sm space-y-10 text-gray-700 leading-relaxed text-[15px]">
          
          {/* Introduction */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5 mb-4">
              <FileText className="w-6 h-6 text-[#e5243b]" />
              Introduction
            </h2>
            <div className="space-y-3">
              {introParagraphs.map((p, idx) => (
                <FormattedText
                  key={idx}
                  text={p}
                  paragraphClassName="text-gray-700 leading-relaxed"
                  linkClassName="text-[#e5243b] underline font-semibold hover:text-[#cf1c31]"
                />
              ))}
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Dynamic Sections */}
          {sections.map((section, idx) => (
            <div key={idx} className="space-y-3">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                {section.title}
              </h2>
              <FormattedText
                text={section.content}
                paragraphClassName="text-gray-700 leading-relaxed"
                linkClassName="text-[#e5243b] underline font-semibold hover:text-[#cf1c31]"
              />
              {idx < sections.length - 1 && <hr className="border-gray-100 mt-8" />}
            </div>
          ))}

          {/* Contact Box */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-6 sm:p-8 mt-10">
            <h3 className="text-xl font-bold mb-2">Have Questions About Our Privacy Policy?</h3>
            <p className="text-gray-300 text-sm mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or your data, please contact our team.
            </p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#e5243b] bg-white px-4 py-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <Mail className="w-4 h-4" /> {contactEmail}
            </a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
